import type { JSX } from "react";

// 任意detailや未知reasonを表示せず、既知の診断だけを翻訳する。
const FIXED_READER_REASON_MESSAGES: Readonly<Record<string, string>> = {
  missing_asset_binding: "承認済み画像が不足しています。",
  missing_background_mapping: "承認済みの背景画像が不足しています。",
  missing_character_variant_mapping: "承認済みの人物差分が不足しています。",
  missing_cutin_mapping: "承認済みのカットインが不足しています。",
  stale_asset_binding: "画像の承認情報と内容が一致しません。",
  asset_digest_unverifiable: "画像の内容を確認できません。",
  ambiguous_asset_binding: "画像の対応を一意に確認できません。",
  ambiguous_background_mapping: "背景画像の対応を一意に確認できません。",
  ambiguous_character_variant_mapping: "人物差分の対応を一意に確認できません。",
  ambiguous_cutin_mapping: "カットインの対応を一意に確認できません。",
  review_artifact_stale: "表示情報の承認を確認できません。",
  public_base_missing: "画像の配信設定を確認できません。",
  public_base_invalid: "画像の配信設定を確認できません。",
  availability_not_configured: "画像の配信設定を確認できません。",
  fixed_version_mismatch: "再生中の版と表示情報が一致しません。",
  fixed_context_required: "同じ版の表示情報をオンラインで再取得してください。",
  invalid_scene_schema: "再生用の表示情報を確認できません。",
  pin_missing: "再生中の版を確認できません。",
  version_unpublished: "再生中の版を利用できません。",
  snapshot_missing: "再生中の版の表示情報がありません。",
  snapshot_invalid: "再生中の版の表示情報を確認できません。",
  digest_mismatch: "再生中の版の内容を確認できません。",
};

export function isFixedReaderReason(reason: string | undefined): boolean {
  return reason !== undefined && Object.hasOwn(FIXED_READER_REASON_MESSAGES, reason);
}

export type ReaderGenerationModeProps = {
  generationMode: string | undefined;
};

/**
 * Reader上の再生方式を、固定再生とリアルタイム生成で明示的に区別する。
 * modeが欠落した旧レスポンスは既存のリアルタイム再生として扱う。
 */
export function ReaderGenerationMode({
  generationMode,
}: ReaderGenerationModeProps): JSX.Element {
  const isFixedContent = generationMode === "pre-baked";
  const label = isFixedContent ? "固定コンテンツ" : "リアルタイム生成";

  return (
    <p
      data-testid="reader-generation-mode"
      data-generation-mode={isFixedContent ? "pre-baked" : "real-time"}
      className="w-fit rounded-full border border-reader-border-strong bg-reader-surface px-3 py-1 text-xs font-medium text-reader-secondary"
    >
      {label}
    </p>
  );
}

/**
 * 固定グラフが欠落・破損した場合の終端表示。
 * AI生成へ戻る操作を置かず、Readerの進行操作をここで止める。
 */
export function FixedContentUnavailable({ reason, onRetry }: { reason?: string | undefined; onRetry?: (() => void) | undefined } = {}): JSX.Element {
  const message = reason !== undefined && isFixedReaderReason(reason)
    ? FIXED_READER_REASON_MESSAGES[reason]
    : "この固定コンテンツは現在利用できません。";
  return (
    <section
      role="alert"
      data-testid="fixed-content-unavailable"
      className="rounded-lg border border-reader-danger-border bg-reader-danger-bg p-4 text-reader-danger-fg"
    >
      <p className="font-semibold">固定コンテンツを再生できません</p>
      <p className="mt-1 text-sm text-reader-secondary">
        {message}
      </p>
      {onRetry !== undefined ? <button type="button" onClick={onRetry} className="mt-3 rounded border border-reader-border px-3 py-2">同じプレイラインを再取得</button> : null}
    </section>
  );
}
